import { post } from '../../networking';
import dataDogLogger from '../datadog';
import { v4 } from 'uuid';
import { COOKIE_KEYS, normalizeEmail, normalizePhone, sha256Hash } from './utils';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export const X_PIXEL_ID = 'o4od6';

export enum XEvent {
    PageView = 'tw-o4od6-rd3u7',
    SunnysideM2 = 'tw-o4od6-rd3tx',
    SunnysideM1 = 'tw-o4od6-rd3ty',
    InitialCheckout = 'tw-o4od6-rd3u3',
    IntakeCompletion = 'tw-o4od6-rd3u1'
}

type XEventProperties = {
    value?: string, // use this to pass the value of the conversion (e.g. 5.00)
    currency?: string, // use this to pass the currency with an ISO 4217 code (e.g. 'USD')
    contents?: {
        content_type?: string,
        content_id?: string,
        content_name?: string,
        content_price?: string,
        num_items?: number,
        content_group_id?: string
    }[]
}

type XClientSideUserData = {
    twclid?: string,
    conversion_id?: string, // unique ID for deduplication (e.g. order id '1a2b3c')
    email_address?: string, // use this to pass a user's email address
    phone_number?: string // phone number in E164 standard
}

type XClientSidePayload = XEventProperties & XClientSideUserData

type XServerSideIdentifiers = {
    twclid?: string,
    hashed_email?: string,
    hashed_phone_number?: string,
    ip_address?: string,
    user_agent?: string
}

export type XServerSidePayload = {
    conversions: XEventProperties & {
        conversion_time?: string,
        event_id?: string,
        event_source_url?: string,
        conversion_id?: string,
        identifiers?: XServerSideIdentifiers[]
    }[]
}

const xClientSideProxy = (eventName: XEvent, properties: XClientSidePayload) => {
    const twq = window && window['twq'];
    if (typeof twq !== 'function') {
        dataDogLogger('error', `X Pixel not initialized when firing event ${eventName}`);
        return;
    }

    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('X Pixel', eventName, properties);
        return;
    }

    twq('event', eventName, properties);
};

const xServerSideProxy = async (eventPayload: XServerSidePayload) => {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('X CAPI', eventPayload.conversions[0].event_id, eventPayload);
        return;
    }

    await post('tracking/x', eventPayload);
};

export default async ({
    event_name,
    user,
    url,
    cookies,
    properties = {}
}: {
    event_name: XEvent,
    user?: any,
    url?: string,
    cookies?: any,
    properties?: XEventProperties
}) => {
    const event_id = v4();

    const clientSidePayload = {
        conversion_id: event_id,
        ...(cookies[COOKIE_KEYS.X_CLICK_ID] ? { twclid: cookies[COOKIE_KEYS.X_CLICK_ID] } : {}),
        ...(user?.email ? { email_address: sha256Hash(normalizeEmail(user?.email)) } : {}),
        ...(user?.phone ? { phone_number: sha256Hash(normalizePhone(user?.phone)) } : {}),
        ...properties
    };
    xClientSideProxy(event_name, clientSidePayload);

    const serverSidePayload = {
        conversions: [{
            event_id: event_name,
            conversion_time: new Date().toISOString(),
            conversion_id: event_id,
            event_source_url: url,
            identifiers: [{
                ...(cookies[COOKIE_KEYS.X_CLICK_ID] ? { twclid: cookies[COOKIE_KEYS.X_CLICK_ID] } : {}),
                ...(user?.email ? { hashed_email: sha256Hash(normalizeEmail(user?.email)) } : {}),
                ...(user?.phone ? { hashed_phone_number: sha256Hash(normalizePhone(user?.phone)) } : {}),
                ...(window.navigator.userAgent ? { user_agent: window.navigator.userAgent } : {})
            }],
            ...properties
        }]
    };

    xServerSideProxy(serverSidePayload);
};
